import UIkit from "uikit";
import { useContext, useEffect, useState } from "react";
import { AppContexts } from "../../providers";
import { Image } from "uikit-react";
import FeatherIcon from "../common/FeatherIcon";
import GuideSectionDeleteDialog from "./section-delete-dialog";
import { useQueuedSave } from "./use-queued-save";

const DELETE_DIALOG_ID = "area-guide-section-delete";

const BuilderAreaGuideSectionsPanel = ({ onSaved, onRegisterSave }) => {
  const { settings, set, get, setSettings } = useContext(
    AppContexts.SettingsContext,
  );

  const [tabs, setTabs] = useState([]);
  const [destination, setDestination] = useState({ tab_order: "" });
  const [newAreaSectionName, setNewAreaSectionName] = useState("");
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const queueSave = useQueuedSave(400);
  useEffect(() => onRegisterSave?.(queueSave), [onRegisterSave, queueSave]);

  const save = (operation) =>
    queueSave(operation)
      .then((result) => {
        if (result) onSaved?.();
        return result;
      })
      .catch(() => {
        UIkit.notification("Unable to save Area Guide changes", "error");
        // Keep the draft visible; the queue retains it for a retry.
        return undefined;
      });

  const setOptimisticSettings = (nextDestination, nextTabs) => {
    setSettings?.((current) => ({
      ...current,
      ...(nextDestination ? { destination: nextDestination } : {}),
      ...(nextTabs ? { destination_tabs: nextTabs } : {}),
    }));
  };

  // Load destination + tabs from backend when needed
  useEffect(() => {
    if (!settings?.destination || !settings?.destination_tabs) {
      get(["destination", "destination_tabs"]);
    }
  }, [settings?.destination, settings?.destination_tabs, get]);

  // Hydrate local state when settings change
  useEffect(() => {
    if (settings?.destination_tabs) {
      setTabs(settings.destination_tabs || []);
    }
    if (settings?.destination) {
      setDestination(settings.destination || { tab_order: "" });
    }
  }, [settings?.destination, settings?.destination_tabs]);

  const order = (destination.tab_order || "").split(",").filter(Boolean);

  // Sorted view: follow tab_order, then any new tabs at the end
  const sortedTabs = [
    ...order
      .map((id) => tabs.find((t) => t.type === id))
      .filter(Boolean),
    ...tabs.filter((t) => order.indexOf(t.type) === -1),
  ];

  // --- Handlers ---

  const handleAreaSectionChange = (type, field, value) => {
    const updatedTabs = tabs.map((t) => {
      if (t.type !== type) return t;

      if (field === "title") {
        return { ...t, label: value };
      }
      if (field === "enabled") {
        return { ...t, show_on_guide: value };
      }
      return t;
    });

    setTabs(updatedTabs);
    setOptimisticSettings(null, updatedTabs);
    save(() => set(["destination", "destination_tabs"], {
      destination,
      destination_tabs: updatedTabs,
    }));
  };

  const handleAddAreaSection = () => {
    const label = (newAreaSectionName || "").trim();
    if (!label) return;

    const type = Math.floor(
      100000000 + Math.random() * (999999999 - 100000000 + 1),
    )
      .toString()
      .trim();

    const newTab = {
      label,
      show_on_embed: false,
      show_on_guide: true,
      isDefault: false,
      type,
    };

    const newTabs = [...tabs, newTab];
    const newOrder = [...order, type].filter(Boolean);

    setTabs(newTabs);
    const nextDestination = { ...destination, tab_order: newOrder.join(",") };
    setDestination(nextDestination);
    setOptimisticSettings(nextDestination, newTabs);

    save(() => set(["destination", "destination_tabs"], {
      destination: nextDestination,
      destination_tabs: newTabs,
    }));

    setNewAreaSectionName(""); // clear input
  };

  const handleRemoveAreaSection = async () => {
    const type = sectionToDelete?.type;
    if (!type) return;

    const newTabs = tabs.filter((t) => t.type !== type);
    const newOrder = order.filter((id) => id !== type);

    setTabs(newTabs);
    const nextDestination = { ...destination, tab_order: newOrder.join(",") };
    setDestination(nextDestination);
    setOptimisticSettings(nextDestination, newTabs);

    await save(() => set(["destination", "destination_tabs"], {
      destination: nextDestination,
      destination_tabs: newTabs,
    }));
    setSectionToDelete(null);
    UIkit.modal(`#${DELETE_DIALOG_ID}`).hide();
  };

  const handleReorder = (ids) => {
    const newOrder = ids.filter(Boolean);
    const nextDestination = { ...destination, tab_order: newOrder.join(",") };
    setDestination(nextDestination);
    setOptimisticSettings(nextDestination, null);
    save(() =>
      set(["destination", "destination_tabs"], {
        destination: nextDestination,
        destination_tabs: tabs,
      }),
    );
  };

  // Hook up UIkit sortable for drag + drop
  useEffect(() => {
    const container = document.querySelector(".area-sections-sortable");
    if (!container) return;

    const onStop = () => {
      const ids = [...container.querySelectorAll("[data-id]")].map((el) =>
        el.getAttribute("data-id"),
      );
      handleReorder(ids);
    };

    container.addEventListener("stop", onStop);
    return () => container.removeEventListener("stop", onStop);
  }, [tabs, destination]);

  return (
    <div>
      {!settings?.destination_tabs ? (
        <div className="builder-panel-loading">
          <span data-uk-spinner="ratio: 1.2" />
          <p>Loading Area Guide sections…</p>
        </div>
      ) : (
        <>
      <p className="secondary uk-margin-small-bottom">
        Group your recommended places into sections. Changes save automatically
        and are live in the guestbook.
      </p>

      <ul
        className="area-sections-sortable builder-section-list"
        uk-sortable="handle: .uk-sortable-handle"
        style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}
      >
        {sortedTabs.map((section) => (
          <li
            key={section.type}
            data-id={section.type}
            className="builder-section-list-item"
          >
            <div className="builder-section-row">
              <div className="uk-flex uk-flex-middle uk-flex-between">
                {/* Section name + drag handle (wide) */}
                <div className="uk-flex uk-flex-middle builder-section-main">
                  <button
                    type="button"
                    className="uk-button hms-simple-icon-btn uk-sortable-handle uk-margin-small-right"
                    uk-tooltip="Drag to reorder sections"
                  >
                    <Image src="/images/move.svg" />
                  </button>

                  <input
                    className="uk-input"
                    style={{ width: "100%" }} // wider section name input
                    type="text"
                    value={section.label || ""}
                    onChange={(e) =>
                      handleAreaSectionChange(
                        section.type,
                        "title",
                        e.target.value,
                      )
                    }
                    placeholder="e.g., Food & Drink"
                  />
                </div>

                <div className="builder-section-actions">
                  {/* Hide toggle */}
                  <div className="uk-width-auto uk-text-center">
                    <label
                      className="setting-switch rbr-checkbox"
                      htmlFor={`areaSectionEnabled-${section.type}`}
                      uk-tooltip="Hide/show this section in the Area Guide"
                    >
                      <input
                        id={`areaSectionEnabled-${section.type}`}
                        type="checkbox"
                        aria-label={`Show ${section.label || "Area Guide section"}`}
                        checked={!!section.show_on_guide}
                        onChange={(e) =>
                          handleAreaSectionChange(
                            section.type,
                            "enabled",
                            e.target.checked,
                          )
                        }
                      />
                      <span className="setting-slider setting-round"></span>
                    </label>
                  </div>

                  {/* Delete icon */}
                  <div className="uk-width-auto uk-text-center">
                    <button
                      className="uk-button hms-simple-icon-btn"
                      type="button"
                      onClick={() => setSectionToDelete(section)}
                      uk-toggle={`target: #${DELETE_DIALOG_ID}`}
                      uk-tooltip="Delete this section"
                      aria-label={`Delete ${section.label || "Area Guide section"}`}
                    >
                      <FeatherIcon name="trash-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="uk-width-1-1 uk-margin-top">
        <div className="add-dest-gd-tab-wrapper">
          <div className="uk-flex uk-flex-middle">
            <input
              className="uk-input"
              type="text"
              placeholder="Section Name"
              value={newAreaSectionName}
              onChange={(e) => setNewAreaSectionName(e.target.value)}
            />
            <div className="plus-w-2">
              <button
                className="uk-button hms-btn hms-gray-btn btn-no-margin"
                type="button"
                onClick={handleAddAreaSection}
              >
                ADD
              </button>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
      <GuideSectionDeleteDialog
        id={DELETE_DIALOG_ID}
        onCancel={() => setSectionToDelete(null)}
        onDelete={handleRemoveAreaSection}
      />
    </div>
  );
};

export default BuilderAreaGuideSectionsPanel;
